/**
 * Phase 3 Feature K — pathway-driven calculator suggestions from KB candidates + extraction.
 */

import { FEATURE_STATUS, TRUST_TIERS, VERIFICATION_STATUS } from './ddxTemporalTaxonomy.js';
import { DDX_CALCULATOR_CATALOG_SEED } from './ddxCalculatorCatalogSeed.js';

/**
 * @typedef {Object} CalculatorSuggestion
 * @property {string} calculatorId
 * @property {string} label
 * @property {string} externalRef
 * @property {string} triggeringDiagnosisId
 * @property {Record<string, boolean|number>} autoFilledInputs
 * @property {string[]} missingInputs
 * @property {number} completionPct
 */

/**
 * @param {import('./ddxTypes.js').ScoredDiagnosis} c
 * @returns {boolean}
 */
function candidateMeetsTrustForCalculator(c) {
  const tier = Number(c.trustTier) || TRUST_TIERS.TIER_1;
  const ver = c.verificationStatus || VERIFICATION_STATUS.VERIFIED;
  if (tier === TRUST_TIERS.TIER_1 && ver === VERIFICATION_STATUS.VERIFIED) return true;
  if (tier === TRUST_TIERS.TIER_2 && ver === VERIFICATION_STATUS.VERIFIED) return true;
  return false;
}

/**
 * @param {import('./ddxTypes.js').ScoredDiagnosis[]} candidates
 * @param {import('./ddxTypes.js').TemporalExtractionResult} extraction
 * @param {object[]} calculatorsCatalog
 * @returns {CalculatorSuggestion[]}
 */
export function suggestCalculators(candidates, extraction, calculatorsCatalog) {
  const catalog = calculatorsCatalog?.length ? calculatorsCatalog : DDX_CALCULATOR_CATALOG_SEED;
  const acuity = extraction?.acuityEstimate || 'indeterminate';
  const present = new Set();
  for (const kf of extraction?.keyFeatures || []) {
    if (kf.featureId && kf.featureStatus === FEATURE_STATUS.PRESENT) present.add(kf.featureId);
  }

  /** @type {CalculatorSuggestion[]} */
  const out = [];
  const seenCalc = new Set();

  for (const calc of catalog) {
    const trig = calc.triggerConditions || {};
    const dxIds = Array.isArray(trig.diagnosisIds) ? trig.diagnosisIds : [];
    const minScore = typeof trig.minimumDxScore === 'number' ? trig.minimumDxScore : 0.3;
    const buckets = Array.isArray(trig.temporalBuckets) ? trig.temporalBuckets : [];

    for (const c of candidates || []) {
      if (!dxIds.includes(c.diagnosisId)) continue;
      if ((c.adjustedScore || 0) < minScore) continue;
      if (buckets.length > 0 && !buckets.includes(acuity)) continue;
      if (!candidateMeetsTrustForCalculator(c)) continue;

      const key = `${calc.id}|${c.diagnosisId}`;
      if (seenCalc.has(key)) continue;
      seenCalc.add(key);

      const inputs = Array.isArray(calc.inputs) ? calc.inputs : [];
      /** @type {Record<string, boolean|number>} */
      const autoFilledInputs = {};
      /** @type {string[]} */
      const missingInputs = [];

      for (const inp of inputs) {
        const maps = Array.isArray(inp.mapToFeatures) ? inp.mapToFeatures : [];
        let filled = false;
        for (const fid of maps) {
          if (present.has(fid)) {
            autoFilledInputs[inp.paramId] = inp.type === 'boolean' ? true : Number(inp.points) || 1;
            filled = true;
            break;
          }
        }
        if (!filled) missingInputs.push(inp.paramId);
      }

      const total = inputs.length || 1;
      const filledCount = total - missingInputs.length;
      const completionPct = filledCount / total;

      out.push({
        calculatorId: calc.id,
        label: calc.label || calc.id,
        externalRef: calc.externalRef || '',
        triggeringDiagnosisId: c.diagnosisId,
        autoFilledInputs,
        missingInputs,
        completionPct,
      });
    }
  }

  out.sort((a, b) => b.completionPct - a.completionPct);
  return out;
}
